import React, { useState, useEffect } from "react"
import Popup from "../../component/popup/popup"
import InputText from "../../component/inputText/inputText"
import PhoneInput from "../../component/phoneInput/phoneInput"
import Grid from "@mui/material/Grid"
import { OfficeData } from "../../models"
import { useSnackbar } from "../../context/snackbarContext"
import { agent } from "../../apiClient/api"

export type OfficePopupMode = "add" | "edit"

type OfficePopupProps = {
    isOpen: boolean
    onClose: () => void
    mode: OfficePopupMode
    office?: OfficeData | null
    agencyId: string
    onSuccess?: () => void
}

const OfficePopup: React.FC<OfficePopupProps> = ({ isOpen, onClose, mode, office, agencyId, onSuccess }) => {
    const { showError, showSuccess } = useSnackbar()

    const [officeName, setOfficeName] = useState("")
    const [address, setAddress] = useState("")
    const [cityName, setCityName] = useState("")
    const [contactNumber, setContactNumber] = useState("")
    const [email, setEmail] = useState("")
    const [errors, setErrors] = useState<{
        officeName?: string
        address?: string
        cityName?: string
        contactNumber?: string
        email?: string
        contactOrEmail?: string
    }>({})
    const [isSaving, setIsSaving] = useState(false)

    const title = mode === "add" ? "Add Office" : "Edit Office"

    useEffect(() => {
        if (isOpen) {
            if (mode === "edit" && office) {
                setOfficeName(office.office_name ?? "")
                setAddress(office.address ?? "")
                setCityName(office.city_name ?? "")
                setContactNumber(office.contact_number ?? "")
                setEmail(office.email ?? "")
            } else {
                setOfficeName("")
                setAddress("")
                setCityName("")
                setContactNumber("")
                setEmail("")
            }
            setErrors({})
        }
    }, [isOpen, mode, office])

    const validate = (): boolean => {
        const next: {
            officeName?: string
            address?: string
            cityName?: string
            contactNumber?: string
            email?: string
            contactOrEmail?: string
        } = {}
        if (!officeName?.trim()) next.officeName = "Office Name is required"
        if (!address?.trim()) next.address = "Address is required"
        if (!cityName?.trim()) next.cityName = "City is required"
        if (contactNumber?.trim() && !/^\+[0-9]{10,15}$/.test(contactNumber.trim()))
            next.contactNumber = "Invalid contact number"
        if (email?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
            next.email = "Invalid email format"
        if (!contactNumber?.trim() && !email?.trim())
            next.contactOrEmail = "Either Contact Number or Email is required"
        setErrors(next)
        return Object.keys(next).length === 0
    }

    const handleSave = async () => {
        if (!validate()) return
        if (mode === "edit" && !office?.office_id) return
        setIsSaving(true)
        setErrors({})
        try {
            const response = await agent.OfficePutAPI.put({
                office_id: mode === "edit" ? office?.office_id ?? "" : null,
                agency_id: agencyId,
                office_name: officeName.trim(),
                address: address.trim(),
                city_name: cityName.trim(),
                contact_number: contactNumber.trim() || null,
                email: email.trim() || null,
                flag_active: mode === "edit" ? office?.flag_active ?? false : true,
            })
            if (response?.status === true) {
                showSuccess(mode === "add" ? "Office added successfully" : "Office updated successfully")
                onSuccess?.()
                onClose()
            } else {
                showError(
                    response?.error?.message ?? (mode === "add" ? "Failed to add office" : "Failed to update office")
                )
            }
        } catch (err) {
            console.error("Office save error", err)
            showError(mode === "add" ? "Failed to add office" : "Failed to update office")
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <Popup
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            primaryButton={{ label: isSaving ? "Saving..." : "Save", onClick: handleSave, disabled: isSaving }}
            secondaryButton={{ label: "Cancel", onClick: onClose }}
            size="medium"
            heightFiexd={true}
        >
            <Grid container spacing={2} style={{ paddingTop: 8 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <InputText
                        label="Office Name"
                        value={officeName}
                        onChange={(e) => setOfficeName(e.target.value)}
                        placeholder="Enter Office Name"
                        required
                        error={errors.officeName}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <InputText
                        label="City"
                        value={cityName}
                        onChange={(e) => setCityName(e.target.value)}
                        placeholder="Enter City Name"
                        required
                        error={errors.cityName}
                    />
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <InputText
                        label="Address"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        placeholder="Enter Address"
                        required
                        error={errors.address}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <PhoneInput
                        label="Contact Number"
                        value={contactNumber}
                        onChange={(value) => setContactNumber(value)}
                        placeholder="Enter Contact Number"
                        international={true}
                        defaultCountry="IN"
                        error={errors.contactNumber}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <InputText
                        label="Email ID"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter Email ID"
                        error={errors.email}
                    />
                </Grid>
                {errors.contactOrEmail && (
                    <Grid size={{ xs: 12 }}>
                        <p style={{ color: "#dc3545", fontSize: 13, margin: 0 }}>{errors.contactOrEmail}</p>
                    </Grid>
                )}
            </Grid>
        </Popup>
    )
}

export default OfficePopup
